import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SuccessPassengerDetails from './SuccessPassengerDetails';
import BookSummary from './Summary';
import { BookingModel } from 'ts/package/interfaces/BookPackageResponse';
import { delay, getBase64Screenshot } from 'utils/utils';
import { submitScreenshots } from 'services/package';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { useLocation } from 'react-router-dom';
import usePackageDataLayer from 'hooks/usePackageDataLayer';
import { b2b, needToScreenshot } from 'constants/generic';

const SuccessPackagePage: FC = () => {
  const [t] = useTranslation();
  
  const successRef = useRef<HTMLDivElement>(null);
  const [order, setOrder] = useState<BookingModel>();
  const [statusGoToMOrReq, setStatusGoToMOrReq] = useState(false);
  const [statusIsOK, setStatusIsOK] = useState(false);
  const { pathname } = useLocation();
  const isTour = useRef(pathname.includes('tour')).current;
  const { pushPackagePurchase } = usePackageDataLayer(isTour);

  useGetPage('ILPackages/BookComplete');

  useEffect(() => {
    if (b2b) {
      if (window.location.pathname.toLowerCase() !== `/ilpackage/success`) {
        return;
      }
    }
    const orderStr = sessionStorage.getItem('packageSuccessOrder');
    if (orderStr) {
      const order = JSON.parse(orderStr);
      setOrder(order);

      const status = order?.order?.order?.segments?.[0]?.roomsStatusCode;
      const orderStatusGoToMOrReq = order?.statusGoToMOrReq;
      setStatusIsOK(status === 'OK');

      setStatusGoToMOrReq(['M', 'RQ'].includes(status) || orderStatusGoToMOrReq);

      (async () => {
        await delay(1000);
        pushPackagePurchase();

        if (needToScreenshot && !order.screenshot) {
          const image = await getBase64Screenshot(successRef.current);

          submitScreenshots({
            screenshots: [],
            orderId: order.orderId,
            lastScreenshot: image,
            transactionId: order.transactionId,
          });

          sessionStorage.setItem(
            'packageSuccessOrder',
            JSON.stringify({ ...order, screenshot: true })
          );
        }
      })();
    }
  }, [pathname]); //eslint-disable-line

  return (
    <div className="success page-wrapper" ref={successRef}>
      <div className="title">
        {t('flight.order_summary')} #{order?.orderId}
      </div>

      {statusIsOK && <div className="text-md mb-20">{t('success')}!</div>}

      {statusGoToMOrReq && (
        <div className="mb-20 text-lh text-center">
          <div>{t('package.status_go_to_m_or_req_1', { number: order?.orderId })}</div>
          <div>{t('package.status_go_to_m_or_req_2')}</div>
        </div>
      )}

      {!statusIsOK && !statusGoToMOrReq && (
        <div className="mb-20">
          <FontAwesomeIcon className="color-danger mr-5" icon={faExclamationTriangle} />
          <span className="text-md">{t('failed_order')}</span>
        </div>
      )}

      {order?.orderHasBeenPaid && (
        <div className="text-md mb-20">{t('hotel.paid_successfully')}</div>
      )}
      {!!order && (
        <SuccessPassengerDetails
          paxes={order.paxes}
          baggagePackage={order?.roomAdditionalPaymentsSelected}
        />
      )}
      {!!order && (
        <BookSummary
          bookingModel={order}
          successPage={true}
          totalPrice={order.totalPrice}
          couponAmount=""
          setCouponAmount={() => {}}
          onClickApply={() => {}}
          additionalPaymentsSelected={[]}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(SuccessPackagePage, 'SuccessPackagePage');
